import { db } from "./firebase";
import { collection, onSnapshot, doc, updateDoc, query, orderBy, where, getDoc } from "firebase/firestore";

/**
 * Listens for real-time updates to the users collection based on the current user's role.
 * @param {object} currentUser - The currently authenticated user object from the auth context.
 * @param {function} successCallback - A function to be called with the array of users.
 * @param {function} errorCallback - An optional function to be called on error.
 * @returns {function} An unsubscribe function.
 */
export const onUsersChange = (currentUser, successCallback, errorCallback) => {
    const usersRef = collection(db, 'users');

    if (!currentUser) {
        successCallback([]);
        return () => {}; // Return a no-op unsubscribe function
    }
    
    // Helper function to sort users, placing the current user's admin (if applicable) at the top.
    const sortUsers = (users, adminId) => {
        return users.sort((a, b) => {
            if (a.id === adminId) return -1;
            if (b.id === adminId) return 1;
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return dateB - dateA;
        });
    };

    if (currentUser.role === 'superadmin') {
        // Super admins can see all users. Simple query, no index needed for default ordering.
        const q = query(usersRef, orderBy("createdAt", "desc"));
        return onSnapshot(q, (snapshot) => {
            const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            successCallback(users);
        }, (error) => {
            console.error("Error listening to user changes:", error);
            if (errorCallback) errorCallback(error);
        });
    }

    if (currentUser.role === 'admin') {
        // Admins see users they created. Removing orderBy to avoid needing a composite index.
        const q = query(usersRef, where("createdBy", "==", currentUser.uid));
        return onSnapshot(q, (snapshot) => {
            let users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sorting client-side
            successCallback(sortUsers(users));
        }, (error) => {
            console.error("Error listening to user changes:", error);
            if (errorCallback) errorCallback(error);
        });
    }

    if (currentUser.role === 'collaborator') {
        const adminId = currentUser.createdBy;
        if (!adminId) {
            successCallback([]);
            return () => {};
        }

        // Query for all users created by the same admin, then filter and sort client-side.
        // This avoids a composite index on (createdBy, role).
        const q = query(usersRef, where("createdBy", "==", adminId));

        return onSnapshot(q, async (snapshot) => {
            const usersCreatedByAdmin = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Filter to get only other collaborators
            const otherCollaborators = usersCreatedByAdmin.filter(user => user.role === 'collaborator' && user.id !== currentUser.uid);

            try {
                // Also fetch the admin's document.
                const adminDocRef = doc(db, 'users', adminId);
                const adminDocSnap = await getDoc(adminDocRef);

                let finalUsers = [...otherCollaborators];
                if (adminDocSnap.exists()) {
                    const adminData = { id: adminDocSnap.id, ...adminDocSnap.data() };
                    finalUsers.push(adminData);
                }
                successCallback(sortUsers(finalUsers, adminId));

            } catch (e) {
                console.error("Error fetching admin user:", e);
                if (errorCallback) errorCallback(e);
                successCallback(sortUsers(otherCollaborators));
            }
        }, (error) => {
            console.error("Error listening to collaborator's user list:", error);
            if (errorCallback) errorCallback(error);
        });
    }

    // Default for other roles (like customer) is to see no one.
    successCallback([]);
    return () => {};
};


/**
 * Updates the role of a specific user.
 * @param {string} userId - The ID of the user to update.
 * @param {string} role - The new role to assign.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 */
export const updateUserRole = (userId, role) => {
    const userDocRef = doc(db, 'users', userId);
    return updateDoc(userDocRef, { role });
};

/**
 * Deactivates a user by marking their status as 'inactive'. This prevents them
 * from logging in.
 * @param {string} userId - The ID of the user to deactivate.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 */
export const deactivateUserAccount = (userId) => {
    const userDocRef = doc(db, 'users', userId);
    return updateDoc(userDocRef, { status: 'inactive' });
};
